import { clsx } from 'clsx';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import type { ChartV2SeriesType } from '../types/chart-v2.types';
import styles from './ChartTypeSelector.module.scss';
import { Chip } from './Chip';
import { AreaIcon, BarIcon, CandleIcon, LineIcon } from './Icons';
import { Toggle } from './Toggle';

/** Single row in the per-series chart-type dropdown. */
export interface ChartTypeSelectorSeriesItem {
  /** Stable identifier — must match the series' `id`. */
  id: string;
  /**
   * Short display for the dropdown row (e.g. asset logo + ticker). Rendered
   * with `pointer-events: none` so any click-handlers embedded in the node
   * don't fire — the row's action is the toggle.
   */
  label: ReactNode;
  /** Current chart type for this series (controls the toggle's value). */
  seriesType: ChartV2SeriesType;
  /** Disables the toggle for this row. */
  disabled?: boolean;
}

/**
 * Props for `<ChartTypeSelector>`.
 *
 * Required: `selected`, `onChange`. Renders the line / candle / bar chips
 * in the top bar; pass `seriesItems` to enable the per-series dropdown.
 */
export interface ChartTypeSelectorProps {
  /**
   * Chart types to render as chips, in display order.
   *
   * @default ['line', 'candlestick', 'bar']
   */
  types?: ChartV2SeriesType[];
  /** Currently selected (global) chart type. */
  selected: ChartV2SeriesType;
  /** Disables every chip in the group. */
  disabled?: boolean;
  /** Subset of `types` to render in a disabled state. */
  disabledTypes?: ChartV2SeriesType[];
  /** Called with the picked chart type when the user clicks a chip. */
  onChange: (seriesType: ChartV2SeriesType) => void;
  /**
   * When provided with more than one entry, clicking a non-line chart
   * type opens a dropdown containing a per-series toggle — mirrors the
   * original chart's "chart type per asset" UX. Omit for a simple
   * global-toggle behaviour.
   */
  seriesItems?: ChartTypeSelectorSeriesItem[];
  /**
   * Called when a toggle inside the per-series dropdown flips. The
   * caller decides how to apply it (usually by updating one series'
   * `seriesType` in its state).
   */
  onSeriesTypeChange?: (
    seriesId: string,
    seriesType: ChartV2SeriesType
  ) => void;
}

const DEFAULT_TYPES: ChartV2SeriesType[] = ['line', 'candlestick', 'bar'];

const ICON_FOR_TYPE: Record<ChartV2SeriesType, React.ReactNode> = {
  line: <LineIcon />,
  area: <AreaIcon />,
  candlestick: <CandleIcon />,
  bar: <BarIcon />,
};

/**
 * Chart-type chip group for the chartv2 top bar: line / candlestick /
 * bar (and optionally area).
 *
 * In single-series mode, clicking a chip applies the type globally via
 * `onChange`. In multi-series mode (when `seriesItems` has more than
 * one entry and `onSeriesTypeChange` is provided), clicking a non-line
 * chip opens a per-series toggle dropdown so each series can carry a
 * distinct chart type.
 */
export const ChartTypeSelector = ({
  types = DEFAULT_TYPES,
  selected,
  disabled,
  disabledTypes = [],
  onChange,
  seriesItems,
  onSeriesTypeChange,
}: ChartTypeSelectorProps) => {
  const [openDropdownType, setOpenDropdownType] =
    useState<ChartV2SeriesType | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const canUsePerSeriesDropdown =
    !!seriesItems && seriesItems.length > 1 && !!onSeriesTypeChange;

  // Close dropdown on outside click / Escape.
  useEffect(() => {
    if (!openDropdownType) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (
        rootRef.current &&
        event.target instanceof Node &&
        !rootRef.current.contains(event.target)
      ) {
        setOpenDropdownType(null);
      }
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenDropdownType(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openDropdownType]);

  const activeSeriesTypes = useMemo(() => {
    if (!seriesItems) {
      return null;
    }

    const counts = { line: 0, candlestick: 0, bar: 0, area: 0 };

    for (const item of seriesItems) {
      counts[item.seriesType] += 1;
    }

    return counts;
  }, [seriesItems]);

  const handleChipClick = (type: ChartV2SeriesType) => {
    // Single-series (or caller didn't opt in to per-series mode): apply
    // globally and close any open dropdown.
    if (!canUsePerSeriesDropdown || type === 'line') {
      setOpenDropdownType(null);
      onChange(type);
      return;
    }

    // Toggle the dropdown for this chip.
    setOpenDropdownType(prev => (prev === type ? null : type));
  };

  return (
    <div
      ref={rootRef}
      className={styles.root}
      data-testid="chartv2-chart-type-selector"
    >
      {types.map(type => {
        const isChipActive =
          activeSeriesTypes !== null && seriesItems && seriesItems.length > 0
            ? activeSeriesTypes[type] === seriesItems.length
            : type === selected;
        const isDropdownOpen = openDropdownType === type;

        return (
          <div key={type} className={styles.chipWrapper}>
            <Chip
              label=""
              leftIcon={ICON_FOR_TYPE[type]}
              isActive={isChipActive || isDropdownOpen}
              disabled={disabled || disabledTypes.includes(type)}
              onClick={() => handleChipClick(type)}
              data-testid={`chartv2-chart-type-${type}`}
            />
            {canUsePerSeriesDropdown && isDropdownOpen && (
              <div
                className={styles.dropdown}
                role="menu"
                data-testid={`chartv2-chart-type-dropdown-${type}`}
              >
                {seriesItems?.map(item => {
                  const isChecked = item.seriesType === type;

                  return (
                    <div
                      key={item.id}
                      className={clsx(
                        styles.dropdownItem,
                        item.disabled && styles.dropdownItemDisabled
                      )}
                      data-testid={`chartv2-chart-type-dropdown-item-${type}-${item.id}`}
                    >
                      <div className={styles.dropdownItemLabel}>
                        {item.label}
                      </div>
                      <Toggle
                        checked={isChecked}
                        disabled={item.disabled}
                        onChange={() => {
                          onSeriesTypeChange?.(
                            item.id,
                            isChecked ? 'line' : type
                          );
                        }}
                        data-testid={`chartv2-chart-type-toggle-${type}-${item.id}`}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
